import { type QueryKey, skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Boards, type GetBoardResponse, type ListBoardsParams } from '@air/api';

import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { PageLimits } from '~/constants/PageLimits';

export const PRIVATE_BOARDS_LIST_KEY = 'PRIVATE_BOARDS_LIST';

export type PrivateBoardsListKey = [typeof PRIVATE_BOARDS_LIST_KEY, UsePrivateBoardsListParams];

export const getPrivateBoardsListKey = (params: UsePrivateBoardsListParams): PrivateBoardsListKey => {
  return [PRIVATE_BOARDS_LIST_KEY, params];
};

export const isPrivateBoardsListKey = (key: QueryKey): key is PrivateBoardsListKey => {
  return Array.isArray(key) && key[0] === PRIVATE_BOARDS_LIST_KEY && isObject(key[1]);
};

export type UsePrivateBoardsListParams = Omit<ListBoardsParams, 'cursor' | 'workspaceId'> & {
  workspaceId: string | undefined;
};

export type UsePrivateBoardsListOptions = { enabled?: boolean };

export const usePrivateBoardsList = (params: UsePrivateBoardsListParams, options?: UsePrivateBoardsListOptions) => {
  const queryClient = useQueryClient();
  const { workspaceId } = params;

  return useInfiniteQuery({
    queryKey: getPrivateBoardsListKey(params),
    queryFn: !workspaceId
      ? skipToken
      : async ({ pageParam }) => {
          const res = await Boards.list({
            cursor: pageParam || null,
            includeAncestors: true,
            limit: params.limit
              ? params.limit
              : pageParam || null
                ? PageLimits.subBoards.more
                : PageLimits.subBoards.init,
            numThumbnails: params.numThumbnails ?? 1,
            ...params,
            workspaceId,
          });

          res.data.forEach((board) => {
            const cachedBoard = queryClient.getQueryCache().get<GetBoardResponse>(
              JSON.stringify(
                getPrivateBoardKey({
                  boardId: board.id,
                }),
              ),
            )?.state.data;

            if (!cachedBoard) {
              queryClient.setQueryData<GetBoardResponse>(getPrivateBoardKey({ boardId: board.id }), board);
            }
          });

          return res;
        },
    enabled: !!workspaceId && options?.enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
    ...options,
  });
};
